import { useState, useCallback, useMemo } from "react";
import {
  Button,
  Group,
  Modal,
  TextInput,
  Stack,
  Text,
  Divider,
  Title,
  NumberInput,
} from "@mantine/core";
import { toast } from "sonner";
import { IconLink, IconLock, IconInfoCircle } from "@tabler/icons-react";

import { useLinkAccountingSoftwareMutation } from "../api/queries/organizationQueries";
import { IntegrationConnectButton } from "./integrations/IntegrationButtons";

interface ViewzFormData {
  token: string;
  entityId: string;
}

const INITIAL_FORM_DATA: ViewzFormData = {
  token: "",
  entityId: "",
};

export function ViewzConnection() {
  const { mutate: linkAccountingSoftware, isPending: isLinking } =
    useLinkAccountingSoftwareMutation();
  const [opened, setOpened] = useState(false);
  const [formData, setFormData] = useState<ViewzFormData>(INITIAL_FORM_DATA);

  const isFormValid = useMemo(
    () =>
      formData.token.trim() !== "" &&
      Number(formData.entityId) > 0,
    [formData],
  );

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpened(false);
    resetForm();
  }, [resetForm]);

  const handleConnect = useCallback(() => {
    if (!isFormValid) {
      toast.error("Please enter your Viewz token and entity ID");
      return;
    }

    const token = formData.token.trim();
    const entityId = Number(formData.entityId);

    linkAccountingSoftware(
      {
        name: "Viewz",
        api_key: token,
        entity_id: entityId,
      },
      {
        onSuccess: () => {
          toast.success("Successfully connected to Viewz");
          resetForm();
          handleCloseModal();
        },
        onError: (err: any) => {
          resetForm();
          const detail = err?.response?.data?.detail;
          const errorMessage =
            (typeof detail === "object" && detail?.message
              ? detail.message
              : typeof detail === "string"
                ? detail
                : null) ||
            err?.message ||
            "Failed to save Viewz credentials";
          toast.error("Failed to connect to Viewz", {
            description: errorMessage,
          });
        },
      },
    );
  }, [
    formData,
    isFormValid,
    linkAccountingSoftware,
    handleCloseModal,
    resetForm,
  ]);

  return (
    <>
      <IntegrationConnectButton onClick={() => setOpened(true)} />

      <Modal
        opened={opened}
        onClose={handleCloseModal}
        title={
          <Group gap="xs">
            <IconLink size={20} />
            <Title order={4}>Connect to Viewz</Title>
          </Group>
        }
        centered
        closeOnClickOutside={!isLinking}
        closeOnEscape={!isLinking}
        size="lg"
        radius="md"
        padding="xl"
      >
        <Stack gap="lg">
          <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
            Visitt Ltd uses Viewz for open AR after reconciliation. Enter the
            Viewz API bearer token and entity ID. Invoices come from the
            receivables aging report; invoice PDFs still come from Priority.
          </Text>

          <Divider />

          <Stack gap="md">
            <TextInput
              label="API token"
              placeholder="Viewz bearer token"
              value={formData.token}
              onChange={(event) =>
                setFormData((previous) => ({
                  ...previous,
                  token: event.currentTarget.value,
                }))
              }
              type="password"
              required
              disabled={isLinking}
              leftSection={<IconLock size={18} />}
              radius="md"
              size="md"
            />
            <NumberInput
              label="Entity ID"
              placeholder="Viewz entityId"
              value={formData.entityId === "" ? "" : Number(formData.entityId)}
              onChange={(value) =>
                setFormData((previous) => ({
                  ...previous,
                  entityId: value === "" || value == null ? "" : String(value),
                }))
              }
              hideControls
              required
              disabled={isLinking}
              radius="md"
              size="md"
            />
          </Stack>

          <Group
            p="md"
            style={{
              backgroundColor: "var(--mantine-color-blue-0)",
              borderRadius: "var(--mantine-radius-md)",
              border: "1px solid var(--mantine-color-blue-2)",
            }}
            gap="xs"
          >
            <IconInfoCircle size={18} color="var(--mantine-color-blue-6)" />
            <Text size="xs" c="dimmed" style={{ flex: 1 }}>
              Credentials are encrypted (AES-256-GCM) and never stored in
              plaintext. Datamint tests the aging endpoint before saving.
            </Text>
          </Group>

          <Divider />

          <Group justify="flex-end" gap="sm" mt="xs">
            <Button
              variant="outline"
              onClick={handleCloseModal}
              disabled={isLinking}
              radius="md"
              size="md"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConnect}
              loading={isLinking}
              disabled={!isFormValid || isLinking}
              leftSection={!isLinking && <IconLink size={16} />}
              radius="md"
              size="md"
            >
              {isLinking ? "Connecting..." : "Connect"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
